import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Euro, Trophy, MapPin, ShieldCheck, Check, ArrowRight,
  Sofa, Package, WashingMachine, Boxes, Store, MoreHorizontal,
} from 'lucide-react';

const FEATURES = [
  { Icon: Euro, label: 'Drivers bid their best price' },
  { Icon: Trophy, label: 'You choose the best offer' },
  { Icon: MapPin, label: 'Track in real time' },
  { Icon: ShieldCheck, label: 'Delivered with care' },
];

const CATEGORIES = [
  { Icon: Sofa, title: 'Furniture', desc: 'Sofas, beds, tables and more' },
  { Icon: Package, title: 'Parcels', desc: 'Small and medium parcels' },
  { Icon: WashingMachine, title: 'Large Items', desc: 'White goods, appliances and bulky items' },
  { Icon: Boxes, title: 'House Moves', desc: 'Single items or full house moves' },
  { Icon: Store, title: 'Business Delivery', desc: 'Stock, supplies and equipment' },
  { Icon: MoreHorizontal, title: 'Other', desc: 'Anything else you need moved' },
];

const BIDDING_POINTS = [
  'See driver profiles and reviews',
  'Compare prices and choose',
  'Secure booking and direct payment to driver',
];

/** Full-width feature strip under the hero. */
export const FeatureStrip = () => (
  <section className="border-y border-gray-100 bg-white" data-testid="hero-feature-row">
    <ul className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-gray-100">
      {FEATURES.map(({ Icon, label }) => (
        <li key={label} className="flex flex-col items-center text-center gap-2 px-4 py-6 sm:py-7">
          <span className="w-12 h-12 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#2E7D32]" strokeWidth={1.75} />
          </span>
          <span className="text-xs sm:text-sm font-bold text-[#050505] leading-snug max-w-[9rem]">{label}</span>
        </li>
      ))}
    </ul>
  </section>
);

/** "Drivers are bidding for your delivery!" showcase. */
export const BiddingShowcase = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 md:py-16" data-testid="bidding-showcase">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
        <div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter leading-[1.05] text-[#050505]">
            Drivers are<br />
            <span className="text-[#F57E0E]">bidding for your delivery!</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#4B5563] max-w-xl">
            Post your job, receive multiple offers from local verified drivers, compare prices and choose the best one.
          </p>
          <ul className="mt-6 space-y-3">
            {BIDDING_POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2E7D32] flex items-center justify-center mt-0.5">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </span>
                <span className="text-sm sm:text-base font-semibold text-[#374151] leading-snug">{p}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={() => navigate('/register')}
            className="mt-7 group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-4 rounded-full bg-[#1B5E20] hover:bg-[#0e3f14] text-white font-bold text-base shadow-lg shadow-emerald-900/10 transition-all active:scale-[0.98]"
            data-testid="bidding-showcase-cta"
          >
            Post a Delivery Job
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
        <div className="relative">
          <img
            src="https://customer-assets.emergentagent.com/job_driver-marketplace-16/artifacts/aj5pezxi_49901.png"
            alt="JustDeliver on phone and laptop — drivers bidding for a delivery"
            className="w-full h-auto max-w-[620px] mx-auto block select-none"
            loading="lazy"
            draggable="false"
            data-testid="bidding-showcase-image"
          />
        </div>
      </div>
    </section>
  );
};

/** "What can you get delivered?" tiles. */
export const CategoryTiles = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 md:py-16" data-testid="category-tiles">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#050505]">What can you get delivered?</h2>
          <p className="text-sm sm:text-base text-gray-500 mt-1">From single items to full loads.</p>
        </div>
        <button
          onClick={() => navigate('/register')}
          className="group inline-flex items-center gap-1.5 text-sm font-bold text-[#1B5E20] hover:text-[#0e3f14] transition-colors"
          data-testid="category-tiles-cta"
        >
          Post your delivery
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
      <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {CATEGORIES.map(({ Icon, title, desc }) => (
          <li
            key={title}
            className="group rounded-2xl border border-gray-200/80 bg-white p-4 text-center hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-900/5 transition-all"
          >
            <span className="mx-auto mb-3 flex w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 items-center justify-center group-hover:bg-emerald-50 group-hover:border-emerald-100 transition-colors">
              <Icon className="w-7 h-7 text-[#2E7D32]" strokeWidth={1.6} />
            </span>
            <p className="text-sm font-extrabold text-[#050505] leading-tight">{title}</p>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-1 leading-snug">{desc}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};
